import * as React from "react";
import Select from "react-select";

export interface SelectOption {
  value: string;
  label: string;
  /** Shorter label for the menu, where a group heading already gives context. */
  menuLabel?: string;
}

export interface SelectOptionGroup {
  label: string;
  options: SelectOption[];
}

interface Props {
  /** Flat options, or groups with headings. */
  options: Array<SelectOption | SelectOptionGroup>;
  /** Selected value, or "" for none. */
  value: string;
  onChange: (value: string) => void;
  /**
   * Label for the "no filter" choice — "All categories", "All batches". Added as
   * the first option, so clearing a filter is a normal pick rather than a
   * react-select clear button the native selects next to it don't have.
   */
  emptyOption?: string;
  /** Used only where there is no empty option, i.e. required fields. */
  placeholder?: string;
  id?: string;
}

const INPUT_COLOR = "#495057";
const BORDER_COLOR = "#ced4da";
/** The caret `.custom-select` draws, so this control's matches the ones beside it. */
const CARET =
  "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='4' height='5' viewBox='0 0 4 5'%3e%3cpath fill='%23343a40' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e\")";

/**
 * A type-to-filter replacement for `<select>` where the list is long enough that
 * scrolling it is the wrong interaction — the category list runs to 137 entries
 * and batches grow with every generation run. `custom-select` styles only the
 * closed control; the popup stays native, so the search has to come from
 * react-select (already a dependency, used by Organization and UserContext).
 *
 * The styles below are Bootstrap 4.5's `.custom-select` and `.dropdown-menu`
 * transcribed, because this sits in a row of plain selects and any difference in
 * height, border, or caret position reads as breakage. Short fixed lists —
 * difficulty, state, flag reason — stay native selects.
 */
export const SearchableSelect: React.FC<Props> = ({
  options,
  value,
  onChange,
  emptyOption,
  placeholder,
  id,
}) => {
  const withEmpty: Array<SelectOption | SelectOptionGroup> = emptyOption
    ? [{ value: "", label: emptyOption }, ...options]
    : options;

  const selected =
    withEmpty
      .flatMap((option) => ("options" in option ? option.options : [option]))
      .find((option) => option.value === value) ?? null;

  return (
    <Select
      inputId={id}
      options={withEmpty}
      value={selected}
      placeholder={placeholder ?? ""}
      menuPlacement="auto"
      // The menu has to escape the filter card's bounds and, in the editor, the
      // modal — hence a portal, above Bootstrap's modal z-index.
      menuPortalTarget={
        typeof document === "undefined" ? undefined : document.body
      }
      formatOptionLabel={(option, meta) => {
        const { label, menuLabel } = option as SelectOption;
        return meta.context === "menu" ? menuLabel ?? label : label;
      }}
      styles={{
        container: (base) => ({ ...base, width: "100%" }),
        control: (base, state) => ({
          ...base,
          minHeight: "calc(1.5em + 0.75rem + 2px)",
          height: "calc(1.5em + 0.75rem + 2px)",
          fontSize: "1rem",
          lineHeight: 1.5,
          color: INPUT_COLOR,
          background: `#fff ${CARET} right 0.75rem center/8px 10px no-repeat`,
          borderColor: state.isFocused ? "#80bdff" : BORDER_COLOR,
          borderRadius: "0.25rem",
          boxShadow: state.isFocused
            ? "0 0 0 0.2rem rgba(0, 123, 255, 0.25)"
            : "none",
          "&:hover": {
            borderColor: state.isFocused ? "#80bdff" : BORDER_COLOR,
          },
        }),
        valueContainer: (base) => ({
          ...base,
          // `.custom-select`'s padding — the right side clears the caret.
          padding: "0.375rem 1.75rem 0.375rem 0.75rem",
        }),
        singleValue: (base) => ({ ...base, margin: 0, color: INPUT_COLOR }),
        placeholder: (base) => ({ ...base, margin: 0, color: "#6c757d" }),
        input: (base) => ({
          ...base,
          margin: 0,
          padding: 0,
          color: INPUT_COLOR,
        }),
        // The caret is drawn by the control's background, so react-select's own
        // indicators and their divider would be a second, misaligned one.
        indicatorsContainer: () => ({ display: "none" }),
        menuPortal: (base) => ({ ...base, zIndex: 1060 }),
        menu: (base) => ({
          ...base,
          marginTop: "2px",
          border: "1px solid rgba(0, 0, 0, 0.15)",
          borderRadius: "0.25rem",
          boxShadow: "0 0.25rem 0.5rem rgba(0, 0, 0, 0.075)",
          fontSize: "1rem",
        }),
        menuList: (base) => ({ ...base, padding: "0.5rem 0" }),
        group: (base) => ({ ...base, padding: 0 }),
        groupHeading: (base) => ({
          ...base,
          // `.dropdown-header`, kept uppercase so headings read as headings.
          padding: "0.5rem 1.5rem",
          margin: 0,
          fontSize: "0.75rem",
          color: "#6c757d",
        }),
        option: (base, state) => ({
          ...base,
          // `.dropdown-item`'s metrics. The selected row is marked with a tint
          // rather than Bootstrap's solid `.active` blue: filters open on their
          // default choice, so a full blue band would greet every open.
          padding: "0.25rem 1.5rem",
          color: "#212529",
          fontWeight: state.isSelected ? 600 : 400,
          backgroundColor: state.isSelected
            ? "#e9ecef"
            : state.isFocused
            ? "#f8f9fa"
            : "transparent",
          cursor: "pointer",
          ":active": { backgroundColor: "#e9ecef" },
        }),
      }}
      onChange={(option) =>
        onChange((option as SelectOption | null)?.value ?? "")
      }
    />
  );
};
